import { printRegistrationSlip } from '../../utils/printUtils';
import PrintButton from '../shared/PrintButton';
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';

// ── DROP 39B-2: EventRegistrationHub — full API wiring ────────────────────────

function erh_getToken(): string {
  return localStorage.getItem('erue_auth_token') || sessionStorage.getItem('erue_auth_token') || '';
}
function erh_authHeaders(): Record<string, string> {
  const t = erh_getToken();
  return t ? { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + t }
           : { 'Content-Type': 'application/json' };
}

type RegistrationType = 'miss-erue' | 'football' | 'quiz' | 'dance' | 'vendor' | 'volunteer';

interface RegistrationConfig {
  id: RegistrationType;
  label: string;
  icon: string;
  description: string;
  fee: number | null;
  feeLabel: string;
  color: string;
  fields: FieldConfig[];
  requiresAuth: boolean;
  requiresPayment: boolean;
}

interface FieldConfig {
  key: string;
  label: string;
  type: 'text' | 'email' | 'tel' | 'textarea' | 'select';
  required: boolean;
  options?: string[];
  placeholder?: string;
}

const S = {
  page:    { background: '#070D0A', minHeight: '100vh', padding: '24px 16px', fontFamily: 'Plus Jakarta Sans, sans-serif' },
  card:    { background: '#0E1812', border: '1px solid #1C2E22', borderRadius: 12, padding: 20 },
  heading: { fontFamily: 'Cinzel, serif', color: '#C9A84C' },
  gold:    '#C9A84C',
  red:     '#ef4444',
  green:   '#22c55e',
  muted:   'rgba(237,232,220,0.5)',
  text:    '#EDE8DC',
  surface: '#121E16',
  border:  '#1C2E22',
};

const REGISTRATION_TYPES: RegistrationConfig[] = [
  {
    id: 'miss-erue',
    label: 'Miss ERUE Pageant',
    icon: '👑',
    description: 'Represent your community in the flagship cultural pageant of ERUE Festival 2026.',
    fee: 5000,
    feeLabel: '₦5,000 registration fee',
    color: '#C9A84C',
    requiresAuth: false,
    requiresPayment: true,
    fields: [
      { key: 'fullName',    label: 'Full Name',           type: 'text',     required: true,  placeholder: 'Your full name' },
      { key: 'email',       label: 'Email Address',       type: 'email',    required: true,  placeholder: 'your@email.com' },
      { key: 'phone',       label: 'Phone Number',        type: 'tel',      required: true,  placeholder: '+234...' },
      { key: 'community',   label: 'Community / Village', type: 'text',     required: true,  placeholder: 'e.g. Jattu-Uzairue' },
      { key: 'age',         label: 'Age',                 type: 'text',     required: true,  placeholder: '18-28' },
      { key: 'bio',         label: 'Brief Bio',           type: 'textarea', required: false, placeholder: 'Tell us about yourself...' },
      { key: 'contestantNo',label: 'Contestant No (admin assigns)', type: 'text', required: false, placeholder: 'Leave blank' },
    ],
  },
  {
    id: 'football',
    label: 'Football Tournament',
    icon: '⚽',
    description: 'Enter your team in the ERUE Kingdom Football Championship.',
    fee: 10000,
    feeLabel: '₦10,000 per team',
    color: '#22c55e',
    requiresAuth: false,
    requiresPayment: true,
    fields: [
      { key: 'fullName',  label: 'Team Captain Name', type: 'text',  required: true,  placeholder: 'Captain full name' },
      { key: 'email',     label: 'Email Address',     type: 'email', required: true,  placeholder: 'your@email.com' },
      { key: 'phone',     label: 'Phone Number',      type: 'tel',   required: true,  placeholder: '+234...' },
      { key: 'teamName',  label: 'Team Name',         type: 'text',  required: true,  placeholder: 'Your team name' },
      { key: 'community', label: 'Community',         type: 'text',  required: true,  placeholder: 'e.g. Iyamho-Uzairue' },
      { key: 'players',   label: 'Number of Players', type: 'text',  required: true,  placeholder: '11-18' },
    ],
  },
  {
    id: 'quiz',
    label: 'Knowledge Quiz',
    icon: '🧠',
    description: 'Test your knowledge of Uzairue Kingdom history and culture.',
    fee: null,
    feeLabel: 'Free entry',
    color: '#60a5fa',
    requiresAuth: false,
    requiresPayment: false,
    fields: [
      { key: 'fullName',  label: 'Full Name',    type: 'text',  required: true, placeholder: 'Your full name' },
      { key: 'email',     label: 'Email',        type: 'email', required: true, placeholder: 'your@email.com' },
      { key: 'phone',     label: 'Phone',        type: 'tel',   required: true, placeholder: '+234...' },
      { key: 'community', label: 'Community',    type: 'text',  required: true, placeholder: 'Your community' },
      { key: 'school',    label: 'School/Org',   type: 'text',  required: false, placeholder: 'Optional' },
    ],
  },
  {
    id: 'dance',
    label: 'Cultural Dance',
    icon: '💃',
    description: 'Showcase your community\'s dance heritage on the grand stage.',
    fee: 3000,
    feeLabel: '₦3,000 per group',
    color: '#c084fc',
    requiresAuth: false,
    requiresPayment: true,
    fields: [
      { key: 'fullName',   label: 'Group Leader Name', type: 'text',     required: true,  placeholder: 'Leader full name' },
      { key: 'email',      label: 'Email',             type: 'email',    required: true,  placeholder: 'your@email.com' },
      { key: 'phone',      label: 'Phone',             type: 'tel',      required: true,  placeholder: '+234...' },
      { key: 'groupName',  label: 'Group/Troupe Name', type: 'text',     required: true,  placeholder: 'Your group name' },
      { key: 'community',  label: 'Community',         type: 'text',     required: true,  placeholder: 'Your community' },
      { key: 'danceStyle', label: 'Dance Style',       type: 'text',     required: true,  placeholder: 'e.g. Traditional Esan' },
      { key: 'members',    label: 'Number of Members', type: 'text',     required: true,  placeholder: 'e.g. 12' },
      { key: 'description',label: 'Performance Notes', type: 'textarea', required: false, placeholder: 'Brief description...' },
    ],
  },
  {
    id: 'vendor',
    label: 'Vendor / Exhibitor',
    icon: '🏪',
    description: 'Set up your stall at the ERUE Festival market and trade fair.',
    fee: 15000,
    feeLabel: '₦15,000 stall fee',
    color: '#fb923c',
    requiresAuth: false,
    requiresPayment: true,
    fields: [
      { key: 'fullName',     label: 'Business Owner Name', type: 'text',  required: true,  placeholder: 'Full name' },
      { key: 'email',        label: 'Email',               type: 'email', required: true,  placeholder: 'your@email.com' },
      { key: 'phone',        label: 'Phone',               type: 'tel',   required: true,  placeholder: '+234...' },
      { key: 'businessName', label: 'Business Name',       type: 'text',  required: true,  placeholder: 'Your business' },
      { key: 'category',     label: 'Product Category',    type: 'select', required: true,
        options: ['Food & Drinks', 'Clothing & Textiles', 'Crafts & Art', 'Electronics', 'Agricultural', 'Services', 'Other'] },
      { key: 'stallSize',    label: 'Stall Size Needed',   type: 'select', required: true,
        options: ['Small (2x2m)', 'Medium (3x3m)', 'Large (4x4m)'] },
    ],
  },
  {
    id: 'volunteer',
    label: 'Volunteer',
    icon: '🤝',
    description: 'Join the ERUE Festival volunteer team and help make history.',
    fee: null,
    feeLabel: 'Free — serve your kingdom',
    color: '#34d399',
    requiresAuth: false,
    requiresPayment: false,
    fields: [
      { key: 'fullName',   label: 'Full Name',       type: 'text',     required: true,  placeholder: 'Your full name' },
      { key: 'email',      label: 'Email',           type: 'email',    required: true,  placeholder: 'your@email.com' },
      { key: 'phone',      label: 'Phone',           type: 'tel',      required: true,  placeholder: '+234...' },
      { key: 'community',  label: 'Community',       type: 'text',     required: true,  placeholder: 'Your community' },
      { key: 'skills',     label: 'Skills / Role',   type: 'select',   required: true,
        options: ['Logistics', 'Media & Photography', 'Guest Relations', 'Security', 'Medical', 'Technical', 'General Support'] },
      { key: 'experience', label: 'Prior Experience',type: 'textarea', required: false, placeholder: 'Any prior volunteer experience?' },
    ],
  },
];

export default function EventRegistrationHub() {
  const { user } = useAuth();
  const _u         = user as unknown as Record<string,unknown>;
  const userRole   = ((_u?.systemRole as string) || (_u?.role as string) || '').toUpperCase();
  const isAdmin    = ['PLATFORM_ADMIN','FESTIVAL_COORDINATOR','SECRETARY'].includes(userRole);

  // ── Registration flow state ───────────────────────────────────────────────
  const [selectedType, setSelectedType] = useState<RegistrationType | null>(null);
  const [step, setStep]                 = useState<'select' | 'form' | 'success'>('select');
  const [formData, setFormData]         = useState<Record<string, string>>({});

  // ── API state ─────────────────────────────────────────────────────────────
  const [apiLoading, setApiLoading]     = useState(false);
  const [apiError, setApiError]         = useState<string | null>(null);
  const [apiSuccess, setApiSuccess]     = useState<string | null>(null);
  const [submittedId, setSubmittedId]   = useState<string | null>(null);

  // ── Admin state ───────────────────────────────────────────────────────────
  const [showAdmin, setShowAdmin]         = useState(false);
  const [adminRegs, setAdminRegs]         = useState<Record<string,unknown>[]>([]);
  const [adminFilter, setAdminFilter]     = useState('all');
  const [adminLoading, setAdminLoading]   = useState(false);
  const [statusLoading, setStatusLoading] = useState<string | null>(null);
  const [adminMsg, setAdminMsg]           = useState<string | null>(null);

  const loadAdminRegs = useCallback(async (filter?: string) => {
    setAdminLoading(true);
    try {
      const f   = filter ?? adminFilter;
      const url = f === 'all'
        ? '/api/admin/event-registrations'
        : '/api/admin/event-registrations?type=' + f;
      const res  = await fetch(url, { headers: erh_authHeaders() });
      const data = await res.json();
      if (data.success) setAdminRegs(data.registrations || []);
    } catch { /* silent */ }
    finally { setAdminLoading(false); }
  }, [adminFilter]);

  useEffect(() => {
    if (showAdmin && isAdmin) loadAdminRegs();
  }, [showAdmin, isAdmin, loadAdminRegs]);

  // ── Paystack return verification ──────────────────────────────────────────
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref    = params.get('reference') || params.get('trxref') || params.get('ref');
    if (ref && ref.startsWith('er_')) {
      verifyRegistration(ref);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  async function verifyRegistration(reference: string) {
    try {
      const res  = await fetch('/api/event-registration/verify', {
        method: 'POST', headers: erh_authHeaders(), body: JSON.stringify({ reference })
      });
      const data = await res.json();
      if (data.success) {
        setApiSuccess('Payment verified! Registration ID: ' + (data.registration?.id || reference) +
          '. Your application is pending admin review.');
        setSubmittedId(data.registration?.id || reference);
        setStep('success');
      }
    } catch { /* silent */ }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedType) return;
    setApiLoading(true); setApiError(null); setApiSuccess(null);
    try {
      const config = REGISTRATION_TYPES.find(r => r.id === selectedType);
      if (!config) throw new Error('Invalid registration type');
      const name  = formData.fullName || formData.teamName || formData.groupName || '';
      const email = formData.email || '';
      if (!name || !email) throw new Error('Name and email are required');
      const res  = await fetch('/api/event-registration', {
        method: 'POST', headers: erh_authHeaders(),
        body: JSON.stringify({
          type:     selectedType,
          name,
          email,
          fee:      config.fee || 0,
          currency: 'NGN',
          formData,
        })
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Submission failed');
      if (data.requiresPayment && data.authorizationUrl) {
        window.location.href = data.authorizationUrl;
        return;
      }
      setSubmittedId(data.registration?.id || null);
      setApiSuccess(data.previewMode
        ? 'Submitted (preview mode). ID: ' + data.registration.id
        : 'Registration submitted! ID: ' + data.registration.id + '. Pending admin review.');
      setStep('success');
    } catch (err: unknown) {
      setApiError(err instanceof Error ? err.message : 'Submission failed. Please try again.');
    } finally {
      setApiLoading(false);
    }
  }

  async function handleAdminStatus(id: string, status: string) {
    setStatusLoading(id);
    setAdminMsg(null);
    try {
      const res  = await fetch('/api/admin/event-registrations/' + id + '/status', {
        method: 'PUT', headers: erh_authHeaders(), body: JSON.stringify({ status })
      });
      const data = await res.json();
      if (data.success) {
        setAdminRegs(prev => prev.map((r: Record<string,unknown>) =>
          r.id === id ? { ...r, status } : r));
        if (status === 'approved') {
          setAdminMsg('✅ Approved — contestant will appear in Voting Booth');
        }
      }
    } catch { /* silent */ }
    finally { setStatusLoading(null); }
  }

  const selectedConfig = REGISTRATION_TYPES.find(r => r.id === selectedType) || null;

  function updateField(key: string, value: string) {
    setFormData(prev => ({ ...prev, [key]: value }));
  }

  // ── STEP: SELECT TYPE ─────────────────────────────────────────────────────
  function SelectStep() {
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
        {REGISTRATION_TYPES.map(rt => (
          <button key={rt.id} onClick={() => { setSelectedType(rt.id); setStep('form'); setFormData({}); setApiError(null); }}
            style={{ ...S.card, textAlign: 'left', cursor: 'pointer', border: '1px solid ' + S.border,
              transition: 'border-color 0.2s', background: '#0E1812' }}>
            <div style={{ fontSize: 32, marginBottom: 10 }}>{rt.icon}</div>
            <h3 style={{ ...S.heading, fontSize: 15, marginBottom: 4 }}>{rt.label}</h3>
            <p style={{ color: S.muted, fontSize: 12, marginBottom: 10, lineHeight: 1.5 }}>{rt.description}</p>
            <div style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, display: 'inline-block',
              background: rt.fee ? 'rgba(201,168,76,0.12)' : 'rgba(34,197,94,0.12)',
              color: rt.fee ? S.gold : S.green }}>
              {rt.feeLabel}
            </div>
          </button>
        ))}
      </div>
    );
  }

  // ── STEP: FORM ────────────────────────────────────────────────────────────
  function FormStep() {
    if (!selectedConfig) return null;
    return (
      <div style={{ maxWidth: 560 }}>
        <button onClick={() => { setStep('select'); setApiError(null); }}
          style={{ background: 'none', border: 'none', color: S.muted, cursor: 'pointer',
            fontSize: 13, marginBottom: 16, padding: 0 }}>
          ← Back to selection
        </button>
        <div style={{ ...S.card, marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <span style={{ fontSize: 28 }}>{selectedConfig.icon}</span>
            <div>
              <h2 style={{ ...S.heading, fontSize: 18, margin: 0 }}>{selectedConfig.label}</h2>
              <p style={{ color: S.muted, fontSize: 12, margin: 0 }}>{selectedConfig.feeLabel}</p>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {selectedConfig.fields.map(field => (
                <div key={field.key}>
                  <label style={{ display: 'block', color: S.text, fontSize: 12,
                    fontWeight: 600, marginBottom: 5 }}>
                    {field.label}{field.required && <span style={{ color: S.gold }}> *</span>}
                  </label>
                  {field.type === 'textarea' ? (
                    <textarea rows={3} placeholder={field.placeholder}
                      value={formData[field.key] || ''}
                      onChange={e => updateField(field.key, e.target.value)}
                      required={field.required}
                      style={{ width: '100%', padding: '10px 14px', borderRadius: 8,
                        border: '1px solid ' + S.border, background: S.surface,
                        color: S.text, fontSize: 13, outline: 'none',
                        resize: 'vertical', boxSizing: 'border-box' }} />
                  ) : field.type === 'select' ? (
                    <select value={formData[field.key] || ''}
                      onChange={e => updateField(field.key, e.target.value)}
                      required={field.required}
                      style={{ width: '100%', padding: '10px 14px', borderRadius: 8,
                        border: '1px solid ' + S.border, background: S.surface,
                        color: formData[field.key] ? S.text : S.muted,
                        fontSize: 13, outline: 'none', boxSizing: 'border-box' }}>
                      <option value="">Select {field.label}</option>
                      {(field.options || []).map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : (
                    <input type={field.type} placeholder={field.placeholder}
                      value={formData[field.key] || ''}
                      onChange={e => updateField(field.key, e.target.value)}
                      required={field.required}
                      style={{ width: '100%', padding: '10px 14px', borderRadius: 8,
                        border: '1px solid ' + S.border, background: S.surface,
                        color: S.text, fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
                  )}
                </div>
              ))}
            </div>

            {apiError && (
              <div style={{ marginTop: 14, padding: '10px 14px', borderRadius: 8,
                background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
                color: S.red, fontSize: 13 }}>⚠️ {apiError}</div>
            )}

            <button type="submit" disabled={apiLoading}
              style={{ marginTop: 18, width: '100%', padding: '12px', borderRadius: 8,
                border: 'none', background: apiLoading ? '#1C2E22' : S.gold,
                color: apiLoading ? S.muted : '#070D0A', fontWeight: 700, fontSize: 15,
                cursor: apiLoading ? 'not-allowed' : 'pointer' }}>
              {apiLoading ? '⏳ Submitting...'
                : selectedConfig.requiresPayment
                  ? 'Pay ' + selectedConfig.feeLabel + ' & Register'
                  : 'Submit Registration'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ── STEP: SUCCESS ─────────────────────────────────────────────────────────
  function SuccessStep() {
    return (
      <div style={{ ...S.card, maxWidth: 520, textAlign: 'center', padding: 40 }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
        <h2 style={{ ...S.heading, fontSize: 22, marginBottom: 8 }}>Registration Submitted!</h2>
        <p style={{ color: S.text, fontSize: 14, marginBottom: 8 }}>
          Your registration is pending admin review.
        </p>
        {submittedId && (
          <div style={{ padding: '8px 16px', borderRadius: 8, background: 'rgba(201,168,76,0.1)',
            border: '1px solid rgba(201,168,76,0.3)', marginBottom: 16 }}>
            <span style={{ color: S.muted, fontSize: 11 }}>Registration ID: </span>
            <span style={{ color: S.gold, fontSize: 12, fontWeight: 700 }}>{submittedId}</span>
          </div>
        )}
        {apiSuccess && (
          <p style={{ color: S.green, fontSize: 12, marginBottom: 20 }}>{apiSuccess}</p>
        )}
        {selectedType === 'miss-erue' && (
          <p style={{ color: S.muted, fontSize: 12, marginBottom: 20 }}>
            👑 Once approved by admin, you will automatically appear in the Miss ERUE Voting Booth.
          </p>
        )}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginBottom: 10 }}>
          <PrintButton
            label="Print Confirmation"
            size="md"
            variant="ghost"
            onClick={() => printRegistrationSlip({
              id: submittedId || undefined,
              type: selectedType || 'general',
              name: (formData as Record<string,string>).fullName || (formData as Record<string,string>).name || '',
              email: (formData as Record<string,string>).email || '',
              phone: (formData as Record<string,string>).phone || (formData as Record<string,string>).phoneNumber || '',
              village: (formData as Record<string,string>).village || (formData as Record<string,string>).community || '',
              state: (formData as Record<string,string>).stateOfOrigin || (formData as Record<string,string>).state || '',
              submittedAt: new Date().toLocaleString('en-NG'),
            })}
          />
        </div>
        <button onClick={() => { setStep('select'); setSelectedType(null); setFormData({}); setApiSuccess(null); setSubmittedId(null); }}
          style={{ padding: '10px 28px', borderRadius: 8, border: 'none',
            background: S.gold, color: '#070D0A', fontWeight: 700, cursor: 'pointer' }}>
          Register Another
        </button>
      </div>
    );
  }

  // ── ADMIN PANEL ───────────────────────────────────────────────────────────
  function AdminPanel() {
    if (!isAdmin) return null;
    return (
      <div style={{ marginTop: 40, borderTop: '1px solid ' + S.border, paddingTop: 28 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
          <h3 style={{ ...S.heading, fontSize: 16, margin: 0 }}>📋 Registration Admin</h3>
          <button onClick={() => setShowAdmin(v => !v)}
            style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid ' + S.border,
              background: showAdmin ? 'rgba(201,168,76,0.1)' : 'transparent',
              color: S.gold, cursor: 'pointer', fontSize: 13 }}>
            {showAdmin ? 'Hide' : 'Show Registrations'}
          </button>
        </div>

        {showAdmin && (
          <div>
            {adminMsg && (
              <div style={{ padding: '8px 14px', borderRadius: 8, marginBottom: 12,
                background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)',
                color: S.green, fontSize: 13 }}>{adminMsg}</div>
            )}

            <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
              {['all','miss-erue','football','quiz','dance','vendor','volunteer'].map(f => (
                <button key={f} onClick={() => { setAdminFilter(f); loadAdminRegs(f); }}
                  style={{ padding: '5px 12px', borderRadius: 6, border: 'none', cursor: 'pointer',
                    fontSize: 12, fontWeight: adminFilter === f ? 700 : 400,
                    background: adminFilter === f ? S.gold : '#1C2E22',
                    color: adminFilter === f ? '#070D0A' : S.muted }}>
                  {f === 'all' ? 'All' : f.replace('-',' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}
                </button>
              ))}
              <button onClick={() => loadAdminRegs()} disabled={adminLoading}
                style={{ padding: '5px 12px', borderRadius: 6, border: '1px solid ' + S.border,
                  background: 'transparent', color: S.muted, cursor: 'pointer', fontSize: 12 }}>
                {adminLoading ? '⏳' : '🔄'} Refresh
              </button>
            </div>

            {adminRegs.length === 0 && !adminLoading && (
              <div style={{ ...S.card, textAlign: 'center', padding: 32 }}>
                <p style={{ color: S.muted, fontSize: 13 }}>
                  No registrations found{adminFilter !== 'all' ? ' for ' + adminFilter : ''}.
                </p>
                {adminFilter === 'miss-erue' && (
                  <p style={{ color: 'rgba(237,232,220,0.3)', fontSize: 11, marginTop: 6 }}>
                    💡 Approve Miss ERUE registrations here — they auto-appear in the Voting Booth
                  </p>
                )}
              </div>
            )}

            {adminRegs.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <p style={{ color: S.muted, fontSize: 12, margin: '0 0 4px' }}>
                  {adminRegs.length} registration{adminRegs.length !== 1 ? 's' : ''}
                </p>
                {adminRegs.map((reg: Record<string,unknown>) => (
                  <div key={reg.id as string} style={{ ...S.card }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3, flexWrap: 'wrap' }}>
                          <span style={{ color: S.text, fontWeight: 700, fontSize: 14 }}>
                            {reg.name as string}
                          </span>
                          <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, fontWeight: 700,
                            background: reg.status === 'approved' ? 'rgba(34,197,94,0.15)'
                              : reg.status === 'rejected' ? 'rgba(239,68,68,0.15)'
                              : 'rgba(201,168,76,0.15)',
                            color: reg.status === 'approved' ? S.green
                              : reg.status === 'rejected' ? S.red
                              : S.gold }}>
                            {(reg.status as string).replace('_',' ').toUpperCase()}
                          </span>
                        </div>
                        <div style={{ color: S.muted, fontSize: 11 }}>
                          {reg.email as string} ·{' '}
                          {(reg.type as string).replace('-',' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}
                          {(reg.fee as number) > 0 && ' · ₦' + (reg.fee as number).toLocaleString()}
                        </div>
                        {reg.type === 'miss-erue' && reg.status === 'approved' && (
                          <div style={{ color: S.green, fontSize: 10, marginTop: 3 }}>
                            ✅ Visible in Voting Booth
                          </div>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                        {reg.status !== 'approved' && (
                          <button onClick={() => handleAdminStatus(reg.id as string, 'approved')}
                            disabled={statusLoading === (reg.id as string)}
                            style={{ padding: '5px 12px', borderRadius: 6, border: 'none',
                              background: 'rgba(34,197,94,0.15)', color: S.green,
                              cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                            {statusLoading === reg.id ? '⏳' : '✅'} Approve
                          </button>
                        )}
                        {reg.status !== 'rejected' && (
                          <button onClick={() => handleAdminStatus(reg.id as string, 'rejected')}
                            disabled={statusLoading === (reg.id as string)}
                            style={{ padding: '5px 12px', borderRadius: 6, border: 'none',
                              background: 'rgba(239,68,68,0.1)', color: S.red,
                              cursor: 'pointer', fontSize: 12 }}>
                            ✕ Reject
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={S.page}>
      <style>{`@keyframes spin { from { transform:rotate(0deg); } to { transform:rotate(360deg); } }`}</style>

      <div style={{ marginBottom: 28 }}>
        <h1 style={{ ...S.heading, fontSize: 28, margin: 0 }}>
          🎯 Event Registrations
        </h1>
        <p style={{ color: S.muted, fontSize: 14, marginTop: 6 }}>
          ERUE Festival of Uzairue Kingdom · December 24–30, 2026
        </p>
      </div>

      {step === 'select'  && <SelectStep />}
      {step === 'form'    && <FormStep />}
      {step === 'success' && <SuccessStep />}

      <AdminPanel />
    </div>
  );
}
